/**
 * PowerInquiryPage Component
 * Main page for searching and querying individual powers across agencies
 * Features multi-agency search, 20+ filters, individual power records, and CSV export
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useOathAuth } from '../../../services/oathAuthContext';
import { usePowerInquiry } from '../../../hooks/usePowerInquiry';
import { DATE_TYPES, VIEW_MODES } from '../../../services/oathPowerInquiryService';
import { addBulkToQueue } from '../../../services/oathDischargeService';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathDualSpinner,
  OathEmptyState,
} from '../../shared/OathUI';
import { FilterPanel, SearchForm, ResultsTable, PowerDetailModal, DischargeModal } from './components';
import GroupedResultsTable from './components/GroupedResultsTable';
import BulkCommentModal from './components/BulkCommentModal';
import { toast } from 'react-toastify';

/**
 * Parse URL search params into initial state object
 */
const parseUrlParams = (searchParams) => {
  const groupFilter = searchParams.get('groupFilter') || null;
  const specificFiltersParam = searchParams.get('specificFilters');
  const specificFilters = specificFiltersParam ? specificFiltersParam.split(',').filter(Boolean) : [];
  const agencyIdsParam = searchParams.get('agencyIds');
  const agencyIds = agencyIdsParam ? agencyIdsParam.split(',').map(Number).filter(id => !isNaN(id)) : [];
  const viewModeParam = searchParams.get('viewMode');
  const viewMode = viewModeParam === VIEW_MODES.BY_REPORT ? VIEW_MODES.BY_REPORT : VIEW_MODES.INDIVIDUAL;

  return {
    groupFilter: groupFilter || null,
    specificFilters,
    agencyIds,
    viewMode,
    defendantLastName: searchParams.get('lastName') || '',
    defendantFirstName: searchParams.get('firstName') || '',
    powerNumber: searchParams.get('powerNo') || '',
    batchId: searchParams.get('batchId') || '',
    reportNo: searchParams.get('reportNo') || '',
    dateType: searchParams.get('dateType') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
  };
};

/**
 * Build URL search params from current state
 */
const buildUrlParams = (state) => {
  const params = new URLSearchParams();

  if (state.groupFilter && state.groupFilter !== 'AllPowers') {
    params.set('groupFilter', state.groupFilter);
  }
  if (state.specificFilters?.length > 0) {
    params.set('specificFilters', state.specificFilters.join(','));
  }
  if (state.agencyIds?.length > 0) {
    params.set('agencyIds', state.agencyIds.join(','));
  }
  if (state.viewMode && state.viewMode !== VIEW_MODES.INDIVIDUAL) {
    params.set('viewMode', state.viewMode);
  }
  if (state.defendantLastName) {
    params.set('lastName', state.defendantLastName);
  }
  if (state.defendantFirstName) {
    params.set('firstName', state.defendantFirstName);
  }
  if (state.powerNumber) {
    params.set('powerNo', state.powerNumber);
  }
  if (state.batchId) {
    params.set('batchId', state.batchId);
  }
  if (state.reportNo) {
    params.set('reportNo', state.reportNo);
  }
  if (state.dateType && state.dateType !== DATE_TYPES.NONE) {
    params.set('dateType', state.dateType);
  }
  if (state.startDate) {
    params.set('startDate', state.startDate);
  }
  if (state.endDate) {
    params.set('endDate', state.endDate);
  }

  return params;
};

/**
 * Power Inquiry Page
 * Allows users to search for individual powers across multiple agencies with various filters
 */
const PowerInquiryPage = () => {
  const { user, isLoading: authLoading } = useOathAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const headerRef = useRef(null);
  const sidebarRef = useRef(null);
  const searchFormRef = useRef(null);
  const [isHeaderOutOfView, setIsHeaderOutOfView] = useState(false);
  const [sidebarFixedStyle, setSidebarFixedStyle] = useState({});
  const [isSearchFormCollapsed, setIsSearchFormCollapsed] = useState(false);
  const [isSearchFormInView, setIsSearchFormInView] = useState(true);
  const [collapseToken, setCollapseToken] = useState(0);
  const [selectedPowers, setSelectedPowers] = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [isBulkDischargeOpen, setIsBulkDischargeOpen] = useState(false);
  const [isBulkCommentOpen, setIsBulkCommentOpen] = useState(false);

  // Parse URL params on mount
  const urlState = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Extract initial state from navigation (e.g., from Dashboard stat click) - takes priority on initial load
  const initialFiltersFromNav = location.state?.initialFilter || null;
  const initialAgencyIdFromNav = location.state?.initialAgencyId;

  // Determine initial filters: URL params take priority, then navigation state
  const initialFilters = useMemo(() => {
    // If URL has filter params, use those
    if (urlState.groupFilter || urlState.specificFilters.length > 0) {
      return {
        groupFilter: urlState.groupFilter,
        specificFilters: urlState.specificFilters,
      };
    }
    // Otherwise use navigation state
    return initialFiltersFromNav;
  }, [urlState.groupFilter, urlState.specificFilters, initialFiltersFromNav]);

  // Get user's agencies
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);

  // Determine initial agency: URL params take priority, then navigation state, then main agency
  const initialAgencyId = useMemo(() => {
    // If URL has agency IDs, use the first one as initial
    if (urlState.agencyIds.length > 0 && agencies.some(a => urlState.agencyIds.includes(a.agencyId))) {
      return urlState.agencyIds[0];
    }
    // Otherwise use navigation state
    if (initialAgencyIdFromNav && agencies.some(a => a.agencyId === initialAgencyIdFromNav)) {
      return initialAgencyIdFromNav;
    }
    // Fall back to main agency, then canPost agency, then first agency
    if (agencies.length > 0) {
      const mainAgency = agencies.find(a => a.isMainAgency);
      const canPostAgency = agencies.find(a => a.canPost);
      const defaultAgency = mainAgency || canPostAgency || agencies[0];
      return defaultAgency.agencyId;
    }
    return null;
  }, [urlState.agencyIds, initialAgencyIdFromNav, agencies]);

  // Determine if we should auto-search on load (URL has any search criteria)
  const autoSearchOnLoad = useMemo(() => {
    return !!(
      urlState.groupFilter ||
      urlState.specificFilters.length > 0 ||
      urlState.defendantLastName ||
      urlState.defendantFirstName ||
      urlState.powerNumber ||
      urlState.batchId ||
      urlState.reportNo ||
      (urlState.dateType && urlState.dateType !== DATE_TYPES.NONE) ||
      urlState.startDate ||
      urlState.endDate
    );
  }, [urlState]);

  // Build initial search criteria from URL params (passed directly to hook to avoid race condition)
  const initialSearchCriteria = useMemo(() => {
    // Only build if we have URL params with values
    if (!autoSearchOnLoad && urlState.agencyIds.length === 0) {
      return null;
    }

    // Filter agency IDs to only include valid ones
    const validAgencyIds = urlState.agencyIds.filter(id =>
      agencies.some(a => a.agencyId === id)
    );

    return {
      agencyIds: validAgencyIds,
      defendantLastName: urlState.defendantLastName,
      defendantFirstName: urlState.defendantFirstName,
      powerNumber: urlState.powerNumber,
      batchId: urlState.batchId,
      reportNo: urlState.reportNo,
      dateType: urlState.dateType || DATE_TYPES.NONE,
      startDate: urlState.startDate,
      endDate: urlState.endDate,
      groupFilter: urlState.groupFilter,
      specificFilters: urlState.specificFilters,
      viewMode: urlState.viewMode,
    };
  }, [urlState, agencies, autoSearchOnLoad]);

  // Use power inquiry hook for state management (pass initial filters and URL state)
  const {
    // Agency selection
    selectedAgencyIds,
    setSelectedAgencyIds,
    toggleAgency,
    selectAllAgencies,
    clearAgencySelection,

    // Search fields
    defendantLastName,
    setDefendantLastName,
    defendantFirstName,
    setDefendantFirstName,
    powerNumber,
    setPowerNumber,
    batchId,
    setBatchId,
    reportNo,
    setReportNo,

    // Date filtering
    dateType,
    setDateType,
    startDate,
    setStartDate,
    endDate,
    setEndDate,

    // Filters
    availableFilters,
    groupFilters,
    specificFilters,
    selectedGroupFilter,
    selectedSpecificFilters,
    isLoadingFilters,
    handleGroupFilterChange,
    handleSpecificFilterToggle,
    groupScopedFilters,
    selectedGroupScopedFilter,
    handleGroupScopedFilterChange,
    setSelectedGroupFilter,
    setSelectedSpecificFilters,
    clearFilters,

    // View mode
    viewMode,
    handleViewModeChange,

    // Results (individual powers)
    results,
    accumulatedResults, // For mobile infinite scroll
    totalCount,
    totalBondAmount,
    pageNumber,
    pageSize,
    totalPages,
    hasNextPage,
    isLoadingMore, // For mobile infinite scroll
    loadMore, // For mobile infinite scroll

    // Results (grouped by report)
    reportGroups,
    totalReports,
    expandedReports,
    loadingReportPowers,
    reportPowersCache,
    toggleReportExpanded,
    loadReportPowers,

    // Sorting
    sortBy,
    sortDescending,
    handleSortChange,

    // Power detail
    selectedPower,
    isLoadingPowerDetail,
    loadPowerDetails,
    closePowerDetail,

    // Loading states
    isSearching,
    isLoadingAggregates,
    isExporting,
    hasSearched,
    hasSearchCriteria,

    // Error
    error,

    // Actions
    search,
    handlePageChange,
    handlePageSizeChange,
    exportToCsv,
    clearSearch,
  } = usePowerInquiry(initialAgencyId, agencies, initialFilters, autoSearchOnLoad, initialSearchCriteria);

  // Clear selection when a new search is initiated (not on page changes or sort changes)
  const prevSearchingRef = useRef(false);
  const isSortTriggeredSearchRef = useRef(false);
  useEffect(() => {
    // Detect when search starts (isSearching goes from false to true) and it's page 1 (new search)
    if (isSearching && !prevSearchingRef.current && pageNumber === 1) {
      if (isSortTriggeredSearchRef.current) {
        // Sort-triggered re-fetch — preserve selections
        isSortTriggeredSearchRef.current = false;
      } else {
        setSelectedPowers(new Set());
        setSelectAllMatching(false);
      }
    }
    prevSearchingRef.current = isSearching;
  }, [isSearching, pageNumber]);

  // Sync state to URL when search criteria changes (after initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const newParams = buildUrlParams({
      groupFilter: selectedGroupFilter,
      specificFilters: selectedSpecificFilters,
      agencyIds: selectedAgencyIds,
      viewMode,
      defendantLastName,
      defendantFirstName,
      powerNumber,
      batchId,
      reportNo,
      dateType,
      startDate,
      endDate,
    });

    // Preserve the detail params if present (power detail modal state)
    const currentDetail = searchParams.get('detail');
    if (currentDetail) {
      newParams.set('detail', currentDetail);
    }
    const currentDetailTab = searchParams.get('detailTab');
    if (currentDetailTab) {
      newParams.set('detailTab', currentDetailTab);
    }
    const currentDoc = searchParams.get('doc');
    if (currentDoc) {
      newParams.set('doc', currentDoc);
    }
    const currentExpandedReport = searchParams.get('expandedReport');
    if (currentExpandedReport) {
      newParams.set('expandedReport', currentExpandedReport);
    }

    // Only update if params changed to avoid unnecessary history entries
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [
    selectedGroupFilter,
    selectedSpecificFilters,
    selectedAgencyIds,
    viewMode,
    defendantLastName,
    defendantFirstName,
    powerNumber,
    batchId,
    reportNo,
    dateType,
    startDate,
    endDate,
    searchParams,
    setSearchParams,
  ]);

  // Track when header scrolls out of view to make filter panel fixed (desktop only)
  useEffect(() => {
    const header = headerRef.current;
    const sidebar = sidebarRef.current;
    if (!header || !sidebar) return;

    // lg breakpoint = 1024px
    const isDesktop = () => window.innerWidth >= 1024;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only apply fixed positioning on desktop screens
        if (!isDesktop()) {
          setIsHeaderOutOfView(false);
          return;
        }

        const isOutOfView = !entry.isIntersecting;
        setIsHeaderOutOfView(isOutOfView);

        // Capture sidebar position when it becomes fixed
        if (isOutOfView) {
          const rect = sidebar.getBoundingClientRect();
          setSidebarFixedStyle({
            position: 'fixed',
            top: '3.5rem', // Below nav bar (56px)
            left: rect.left,
            width: rect.width,
            maxHeight: 'calc(100vh - 4rem)',
            overflowY: 'auto',
          });
        }
      },
      { threshold: 0, rootMargin: '-56px 0px 0px 0px' } // Account for nav bar height
    );

    // Re-evaluate on resize (e.g., when switching between mobile/desktop)
    const handleResize = () => {
      if (!isDesktop()) {
        setIsHeaderOutOfView(false);
      }
    };

    observer.observe(header);
    window.addEventListener('resize', handleResize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Track when search form scrolls out of view (for fixed header/footer visibility)
  useEffect(() => {
    const searchForm = searchFormRef.current;
    if (!searchForm) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSearchFormInView(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-56px 0px 0px 0px' } // Account for nav bar
    );

    observer.observe(searchForm);
    return () => observer.disconnect();
  }, []);

  // Wrap toggleReportExpanded to also sync expandedReport param to URL
  const handleToggleReportExpanded = useCallback((reportNo) => {
    toggleReportExpanded(reportNo);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      // If it was already expanded, we're collapsing it — remove param
      if (expandedReports.has(reportNo)) {
        next.delete('expandedReport');
      } else {
        next.set('expandedReport', reportNo);
      }
      return next;
    }, { replace: true });
  }, [toggleReportExpanded, expandedReports, setSearchParams]);

  // On mount, if URL has an expandedReport param, auto-expand it once results are loaded
  const expandedReportFromUrl = searchParams.get('expandedReport');
  const expandedReportLoadedRef = useRef(false);
  useEffect(() => {
    if (expandedReportFromUrl && !expandedReportLoadedRef.current && reportGroups.length > 0) {
      expandedReportLoadedRef.current = true;
      if (!expandedReports.has(expandedReportFromUrl)) {
        toggleReportExpanded(expandedReportFromUrl);
      }
    }
  }, [expandedReportFromUrl, reportGroups, expandedReports, toggleReportExpanded]);

  // Handle viewing power details - also sync to URL so refresh preserves the modal
  const handleViewPowerDetails = useCallback((powerNo) => {
    loadPowerDetails(powerNo);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('detail', powerNo);
      return next;
    }, { replace: true });
  }, [loadPowerDetails, setSearchParams]);

  // Wrap closePowerDetail to also remove URL param
  const handleClosePowerDetail = useCallback(() => {
    closePowerDetail();
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('detail');
      next.delete('detailTab');
      next.delete('doc');
      return next;
    }, { replace: true });
  }, [closePowerDetail, setSearchParams]);

  // On mount, if URL has a detail param, auto-open the modal
  const detailFromUrl = searchParams.get('detail');
  const detailLoadedRef = useRef(false);
  useEffect(() => {
    if (detailFromUrl && !detailLoadedRef.current) {
      detailLoadedRef.current = true;
      loadPowerDetails(detailFromUrl);
    }
  }, [detailFromUrl, loadPowerDetails]);

  // Handle sort change - state update triggers the useEffect below
  const handleSort = useCallback((field, descending) => {
    handleSortChange(field, descending);
  }, [handleSortChange]);

  // Trigger search after sort state has been applied and search is recreated with new values
  const prevSortRef = useRef({ sortBy, sortDescending });
  useEffect(() => {
    if (prevSortRef.current.sortBy !== sortBy || prevSortRef.current.sortDescending !== sortDescending) {
      prevSortRef.current = { sortBy, sortDescending };
      if (hasSearched) {
        isSortTriggeredSearchRef.current = true;
        search(1);
      }
    }
  }, [sortBy, sortDescending, search, hasSearched]);

  // Wrapper for page change that also collapses search form
  const handlePageChangeWithCollapse = useCallback((page) => {
    setCollapseToken(prev => prev + 1);
    handlePageChange(page);
  }, [handlePageChange]);

  // Wrapper for page size change that also collapses search form
  const handlePageSizeChangeWithCollapse = useCallback((size) => {
    setCollapseToken(prev => prev + 1);
    handlePageSizeChange(size);
  }, [handlePageSizeChange]);

  // Power selection handlers
  const handlePowerSelectionChange = useCallback((powerNo, isSelected) => {
    // Clear "select all matching" when individual selection changes
    setSelectAllMatching(false);
    setSelectedPowers(prev => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(powerNo);
      } else {
        next.delete(powerNo);
      }
      return next;
    });
  }, []);

  const handleSelectAllPowers = useCallback((powerNos, isSelected) => {
    if (isSelected) {
      // When selecting all via header checkbox, select ALL matching (not just current page)
      setSelectAllMatching(true);
      // Also add current page to selectedPowers for visual consistency
      setSelectedPowers(new Set(powerNos));
    } else {
      // When unchecking, clear everything
      setSelectAllMatching(false);
      setSelectedPowers(new Set());
    }
  }, []);

  const handleSelectAllMatching = useCallback((isSelected) => {
    setSelectAllMatching(isSelected);
    if (!isSelected) {
      // When deselecting "all matching", clear the selection entirely
      setSelectedPowers(new Set());
    }
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedPowers(new Set());
    setSelectAllMatching(false);
  }, []);

  // Power action handlers
  const handlePowerUpdated = useCallback((powerNo) => {
    loadPowerDetails(powerNo);
    search(pageNumber);
  }, [loadPowerDetails, search, pageNumber]);

  const handleBulkDischargeSuccess = useCallback(() => {
    setIsBulkDischargeOpen(false);
    handleClearSelection();
    search(1);
  }, [search, handleClearSelection]);

  const handleBulkCommentSuccess = useCallback(() => {
    setIsBulkCommentOpen(false);
    handleClearSelection();
  }, [handleClearSelection]);

  const handleAddToDischargeQueue = useCallback(async () => {
    if (selectedPowers.size === 0) return;

    // Build lookup from all available power data so we can send details
    const powerLookup = new Map();
    results.forEach((p) => powerLookup.set(p.powerNo, p));
    accumulatedResults.forEach((p) => powerLookup.set(p.powerNo, p));
    Object.values(reportPowersCache).forEach((powers) => {
      if (Array.isArray(powers)) {
        powers.forEach((p) => powerLookup.set(p.powerNo, p));
      }
    });

    // Build array with details for the queue
    const powersWithDetails = Array.from(selectedPowers).map((powerNo) => {
      const p = powerLookup.get(powerNo);
      if (!p) return powerNo; // fallback to string if not found
      return {
        powerNo: p.powerNo,
        defendantName: p.defendantFullName || `${p.defendantLastName || ''}, ${p.defendantFirstName || ''}`.trim() || null,
        bondAmount: p.bondAmount ?? p.powerAmount ?? null,
        status: p.powerStatus || null,
      };
    });

    try {
      const result = await addBulkToQueue(powersWithDetails, { agencyId: selectedAgencyIds?.[0] });
      const addedCount = result.data?.addedCount ?? 0;
      const invalidCount = result.data?.invalidCount ?? 0;
      const validations = result.data?.validations || [];

      if (result.success && addedCount > 0) {
        toast.success(
          <div>
            <strong>{addedCount}</strong> power{addedCount !== 1 ? 's' : ''} added to discharge queue.{' '}
            <button
              onClick={() => navigate('/app/discharges')}
              className="underline font-semibold"
            >
              Go to Discharges
            </button>
          </div>,
          { autoClose: 6000 },
        );
        handleClearSelection();
      } else if (invalidCount > 0) {
        // All powers failed validation — show the first unique error
        const uniqueErrors = [...new Set(validations.flatMap((v) => v.validationErrors || []))];
        toast.error(
          <div>
            <strong>{invalidCount}</strong> power{invalidCount !== 1 ? 's' : ''} could not be added.
            {uniqueErrors.length > 0 && (
              <p className="mt-1 text-xs opacity-90">{uniqueErrors.join('; ')}</p>
            )}
          </div>,
          { autoClose: 8000 },
        );
      } else {
        toast.error(result.error || 'Failed to add powers to discharge queue.');
      }
    } catch (err) {
      console.error('Add to discharge queue error:', err);
      toast.error('An error occurred while adding to the discharge queue.');
    }
  }, [selectedPowers, results, accumulatedResults, reportPowersCache, navigate, handleClearSelection]);

  // Compute filter badges for collapsed search form header
  const filterBadges = useMemo(() => {
    const badges = [];

    // Add group filter badge (if not "All Powers")
    if (selectedGroupFilter && selectedGroupFilter !== 'AllPowers') {
      const groupFilter = groupFilters.find(f => f.filterName === selectedGroupFilter);
      if (groupFilter) {
        badges.push({
          label: groupFilter.label || groupFilter.filterDescription || selectedGroupFilter,
          type: 'group',
        });
      }
    }

    // Add specific filter badges (includes type 2 specific filters and type 3 group-scoped filters like AOL buckets)
    selectedSpecificFilters.forEach(filterName => {
      const filter = specificFilters.find(f => f.filterName === filterName)
        || availableFilters.find(f => f.filterName === filterName);
      if (filter) {
        badges.push({
          label: filter.label || filter.filterDescription || filterName,
          type: 'specific',
        });
      }
    });

    return badges;
  }, [selectedGroupFilter, selectedSpecificFilters, groupFilters, specificFilters, availableFilters]);

  // Loading state
  if (authLoading) {
    return (
      <OathPageWrapper maxWidth="max-w-[1600px]" responsive>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <OathDualSpinner size="lg" className="mx-auto" />
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading Power Inquiry...</p>
          </div>
        </div>
      </OathPageWrapper>
    );
  }

  // No agencies
  if (!agencies.length) {
    return (
      <OathPageWrapper maxWidth="max-w-[1600px]" responsive>
        <OathContentCard>
          <OathEmptyState
            title="No Agency Access"
            message="You do not have access to any agencies for Power Inquiry."
          />
        </OathContentCard>
      </OathPageWrapper>
    );
  }

  return (
    <OathPageWrapper maxWidth="max-w-[1600px]" responsive>
      {/* Compact Header */}
      <div ref={headerRef} className="mb-6 qhd:mb-8 4k:mb-10">
        <OathHeaderCard
          title="Power Inquiry"
          subtitle="Search individual powers across your agencies"
          icon={MagnifyingGlassIcon}
        />
      </div>

      {/* Main Content - Sidebar + Content Layout */}
      <div className="flex flex-col lg:flex-row gap-6 qhd:gap-8 4k:gap-10">
        {/* Left Sidebar - Filters (fixed on desktop after header scrolls out) */}
        <aside ref={sidebarRef} className="lg:w-72 xl:w-80 qhd:w-96 4k:w-[480px] flex-shrink-0">
          <div style={isHeaderOutOfView ? sidebarFixedStyle : undefined}>
            <FilterPanel
              groupFilters={groupFilters}
              specificFilters={specificFilters}
              selectedGroupFilter={selectedGroupFilter}
              selectedSpecificFilters={selectedSpecificFilters}
              onGroupFilterChange={handleGroupFilterChange}
              onSpecificFilterToggle={handleSpecificFilterToggle}
              onClearFilters={clearFilters}
              isLoading={isLoadingFilters}
              roundedTop={!isHeaderOutOfView}
            />
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 space-y-6 qhd:space-y-8 4k:space-y-10">
          {/* Search Form */}
          <div ref={searchFormRef}>
            <SearchForm
              agencies={agencies}
              selectedAgencyIds={selectedAgencyIds}
              onToggleAgency={toggleAgency}
              onSelectAllAgencies={selectAllAgencies}
              onClearAgencySelection={clearAgencySelection}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              defendantLastName={defendantLastName}
              onDefendantLastNameChange={setDefendantLastName}
              defendantFirstName={defendantFirstName}
              onDefendantFirstNameChange={setDefendantFirstName}
              powerNumber={powerNumber}
              onPowerNumberChange={setPowerNumber}
              batchId={batchId}
              onBatchIdChange={setBatchId}
              reportNo={reportNo}
              onReportNoChange={setReportNo}
              dateType={dateType}
              onDateTypeChange={setDateType}
              startDate={startDate}
              onStartDateChange={setStartDate}
              endDate={endDate}
              onEndDateChange={setEndDate}
              selectedGroupFilter={selectedGroupFilter}
              groupScopedFilters={groupScopedFilters}
              selectedGroupScopedFilter={selectedGroupScopedFilter}
              onGroupScopedFilterChange={handleGroupScopedFilterChange}
              onSearch={() => search(1)}
              onClear={clearSearch}
              onExport={exportToCsv}
              isSearching={isSearching}
              isExporting={isExporting}
              hasSearchCriteria={hasSearchCriteria}
              hasResults={hasSearched && (viewMode === VIEW_MODES.BY_REPORT ? reportGroups.length > 0 : results.length > 0)}
              onCollapseChange={setIsSearchFormCollapsed}
              collapseToken={collapseToken}
              filterBadges={filterBadges}
            />
          </div>

          {/* Summary Stats Bar - Only show when we have results */}
          {hasSearched && (viewMode === VIEW_MODES.BY_REPORT ? reportGroups.length > 0 : results.length > 0) && (
            <div className="relative bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20 rounded-xl qhd:rounded-2xl border border-primary/20 dark:border-primary/30 p-4 qhd:p-6 4k:p-8 overflow-hidden">
              {/* Subtle animated gradient bar at top */}
              {isLoadingAggregates && (
                <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden">
                  <div className="h-full w-full bg-gradient-to-r from-transparent via-primary/70 to-transparent animate-pulse" />
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-4 qhd:gap-6">
                <div className="flex items-center gap-6 qhd:gap-8 4k:gap-10">
                  {viewMode === VIEW_MODES.BY_REPORT && (
                    <>
                      <div>
                        <p className="text-xs qhd:text-sm 4k:text-base font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Reports</p>
                        <div className="flex items-center gap-2">
                          <p className="text-2xl qhd:text-3xl 4k:text-4xl font-bold text-gray-900 dark:text-white">
                            {totalReports.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="h-10 qhd:h-12 4k:h-14 w-px bg-gray-300 dark:bg-gray-600" />
                    </>
                  )}
                  <div>
                    <p className="text-xs qhd:text-sm 4k:text-base font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Powers</p>
                    <div className="flex items-center gap-2">
                      <p className={`text-2xl qhd:text-3xl 4k:text-4xl font-bold text-gray-900 dark:text-white transition-all duration-300 ${isLoadingAggregates ? 'opacity-60' : ''}`}>
                        {totalCount.toLocaleString()}
                      </p>
                      {isLoadingAggregates && (
                        <div className="w-4 h-4 qhd:w-5 qhd:h-5 border-2 border-gray-300 dark:border-gray-500 border-t-primary rounded-full animate-spin" />
                      )}
                    </div>
                  </div>
                  <div className="h-10 qhd:h-12 4k:h-14 w-px bg-gray-300 dark:bg-gray-600" />
                  <div>
                    <p className="text-xs qhd:text-sm 4k:text-base font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {viewMode === VIEW_MODES.BY_REPORT ? 'Open Liability' : 'Total Bond Amount'}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className={`text-2xl qhd:text-3xl 4k:text-4xl font-bold text-green-600 dark:text-green-400 transition-all duration-300 ${isLoadingAggregates ? 'opacity-60' : ''}`}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalBondAmount)}
                      </p>
                      {isLoadingAggregates && (
                        <div className="w-4 h-4 qhd:w-5 qhd:h-5 border-2 border-gray-300 dark:border-gray-500 border-t-green-500 rounded-full animate-spin" />
                      )}
                    </div>
                  </div>
                  {/* Selection Counter */}
                  {(selectedPowers.size > 0 || selectAllMatching) && (
                    <>
                      <div className="h-10 qhd:h-12 4k:h-14 w-px bg-gray-300 dark:bg-gray-600" />
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="text-xs qhd:text-sm 4k:text-base font-medium text-primary dark:text-primary-light uppercase tracking-wide">Selected</p>
                          <div className="flex items-center gap-2">
                            <CheckIcon className="h-5 w-5 qhd:h-6 qhd:w-6 text-primary dark:text-primary-light" />
                            <p className="text-2xl qhd:text-3xl 4k:text-4xl font-bold text-primary dark:text-primary-light">
                              {selectAllMatching ? totalCount.toLocaleString() : selectedPowers.size.toLocaleString()}
                            </p>
                            {selectAllMatching && (
                              <span className="text-xs qhd:text-sm text-primary/70 dark:text-primary-light/70">(all)</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleClearSelection}
                          className="p-1.5 qhd:p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Clear selection"
                        >
                          <XMarkIcon className="h-5 w-5 qhd:h-6 qhd:w-6" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="text-sm qhd:text-base 4k:text-lg text-gray-500 dark:text-gray-400">
                  {viewMode === VIEW_MODES.BY_REPORT ? (
                    <>Showing {reportGroups.length.toLocaleString()} of {totalReports.toLocaleString()} reports</>
                  ) : (
                    <>
                      Showing {results.length.toLocaleString()} of{' '}
                      <span className={`transition-opacity duration-200 ${isLoadingAggregates ? 'opacity-60' : ''}`}>
                        {totalCount.toLocaleString()}
                      </span>
                      {' '}powers
                    </>
                  )}
                  {isLoadingAggregates && (
                    <span className="ml-2 inline-flex items-center text-xs text-primary dark:text-primary-light">
                      <span className="w-2 h-2 mr-1 border border-primary border-t-transparent rounded-full animate-spin" />
                      updating
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Results - Individual Powers or Grouped by Report */}
          {viewMode === VIEW_MODES.BY_REPORT ? (
            <GroupedResultsTable
              reportGroups={reportGroups}
              totalReports={totalReports}
              totalPowers={totalCount}
              pageNumber={pageNumber}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={handlePageChangeWithCollapse}
              onPageSizeChange={handlePageSizeChangeWithCollapse}
              expandedReports={expandedReports}
              loadingReportPowers={loadingReportPowers}
              reportPowersCache={reportPowersCache}
              onToggleExpanded={handleToggleReportExpanded}
              onLoadReportPowers={loadReportPowers}
              onViewPowerDetails={handleViewPowerDetails}
              sortBy={sortBy}
              sortDescending={sortDescending}
              onSortChange={handleSort}
              isLoading={isSearching}
              hasSearched={hasSearched}
              error={error}
              enableFixedHeaderFooter={isSearchFormCollapsed || !isSearchFormInView}
              selectedPowers={selectedPowers}
              onSelectionChange={handlePowerSelectionChange}
              selectAllMatching={selectAllMatching}
              onSelectAllMatching={handleSelectAllMatching}
              onBulkDischarge={() => setIsBulkDischargeOpen(true)}
              onAddToDischargeQueue={handleAddToDischargeQueue}
              onBulkComment={() => setIsBulkCommentOpen(true)}
            />
          ) : (
            <ResultsTable
              agencyId={initialAgencyId}
              results={results}
              accumulatedResults={accumulatedResults}
              totalCount={totalCount}
              pageNumber={pageNumber}
              pageSize={pageSize}
              totalPages={totalPages}
              hasNextPage={hasNextPage}
              onPageChange={handlePageChangeWithCollapse}
              onPageSizeChange={handlePageSizeChangeWithCollapse}
              onLoadMore={loadMore}
              sortBy={sortBy}
              sortDescending={sortDescending}
              onSortChange={handleSort}
              onViewPowerDetails={handleViewPowerDetails}
              isLoading={isSearching}
              isLoadingMore={isLoadingMore}
              isLoadingAggregates={isLoadingAggregates}
              hasSearched={hasSearched}
              enableFixedHeaderFooter={isSearchFormCollapsed || !isSearchFormInView}
              error={error}
              selectedPowers={selectedPowers}
              onSelectionChange={handlePowerSelectionChange}
              onSelectAll={handleSelectAllPowers}
              selectAllMatching={selectAllMatching}
              onSelectAllMatching={handleSelectAllMatching}
              onBulkDischarge={() => setIsBulkDischargeOpen(true)}
              onAddToDischargeQueue={handleAddToDischargeQueue}
              onBulkComment={() => setIsBulkCommentOpen(true)}
            />
          )}
        </main>
      </div>

      {/* Power Detail Modal */}
      <PowerDetailModal
        power={selectedPower}
        isOpen={!!selectedPower || isLoadingPowerDetail}
        onClose={handleClosePowerDetail}
        isLoading={isLoadingPowerDetail}
        onPowerUpdated={handlePowerUpdated}
      />

      {/* Bulk Discharge Modal */}
      <DischargeModal
        isOpen={isBulkDischargeOpen}
        onClose={() => setIsBulkDischargeOpen(false)}
        selectedPowers={selectedPowers}
        selectAllMatching={selectAllMatching}
        totalMatchingCount={totalCount}
        onSuccess={handleBulkDischargeSuccess}
        onPowerRemoved={(powerNo) => handlePowerSelectionChange(powerNo, false)}
        agencyId={selectedAgencyIds?.[0] || null}
      />

      {/* Bulk Comment Modal */}
      <BulkCommentModal
        isOpen={isBulkCommentOpen}
        onClose={() => setIsBulkCommentOpen(false)}
        selectedPowers={selectedPowers}
        onSuccess={handleBulkCommentSuccess}
      />
    </OathPageWrapper>
  );
};

export default PowerInquiryPage;
